// ════════════════════════════════════════════════════════════════
// LICENSE STORE — Pro upgrade state (Zustand)
// ════════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { db } from '../db/database';

interface LicenseState {
  isPro: boolean;
  licenseKey: string | null;
  isValidating: boolean;

  checkStoredLicense: () => Promise<void>;
  validateLicense: (key: string, workerUrl?: string) => Promise<boolean>;
  validateSession: (sessionId: string, workerUrl?: string) => Promise<boolean>;
  activateLocally: (key: string) => void;
}

export const useLicenseStore = create<LicenseState>((set) => ({
  isPro: false,
  licenseKey: null,
  isValidating: false,

  checkStoredLicense: async () => {
    const stored = await db.appMeta.get('proLicenseKey');
    if (stored) {
      set({ isPro: true, licenseKey: stored.value });
    }
  },

  validateLicense: async (key: string, workerUrl?: string) => {
    set({ isValidating: true });

    // If we have a worker URL, validate server-side
    if (workerUrl) {
      try {
        const res = await fetch(workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'validate_license', licenseKey: key }),
        });
        const data = await res.json();
        if (data.valid) {
          await db.appMeta.put({ key: 'proLicenseKey', value: key });
          set({ isPro: true, licenseKey: key, isValidating: false });
          return true;
        }
      } catch {
        // Network failed — fall through to local activation
      }
    }

    // Local activation (offline grace period)
    // Store the key; trust it locally until next online validation
    await db.appMeta.put({ key: 'proLicenseKey', value: key });
    set({ isPro: true, licenseKey: key, isValidating: false });
    return true;
  },

  validateSession: async (sessionId: string, workerUrl?: string) => {
    set({ isValidating: true });
    if (!workerUrl) {
      set({ isValidating: false });
      return false;
    }

    try {
      const res = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate_session', sessionId }),
      });
      const data = await res.json();

      if (data.valid && data.licenseKey) {
        await db.appMeta.put({ key: 'proLicenseKey', value: data.licenseKey });
        set({ isPro: true, licenseKey: data.licenseKey, isValidating: false });
        return true;
      }
    } catch {
      // Network error
    }

    set({ isValidating: false });
    return false;
  },

  activateLocally: (key: string) => {
    db.appMeta.put({ key: 'proLicenseKey', value: key });
    set({ isPro: true, licenseKey: key });
  },
}));
